import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { CircularProgress, LinearProgress, Box, Button, Tab, Tabs } from '@mui/material'
import { studentApiSlice } from '../../../features/student/studentSlice'
import { H3 } from '../../../layouts/TextStyles'
import { userApiSlice } from '../../../features/user/userSlice'
import CustomInput from '../../../components/muiComponents/customInput'
import StudentsTable from './components/StudentsTable'
import AddStudentDialog from './components/AddStudentDialog'
import SingleSelect from '../../../components/muiComponents/singleSelect/SingleSelect'
import { courseApiSlice } from '../../../features/course/courseSlice'
import { subjectApiSlice } from '../../../features/subject/subjectSlice'
import { NewStudentState } from '../../../features/types'
import { DEFAULT_PAGE_SIZE } from '../../../utils/constants'
import { useInfiniteScroll } from '../../../app/hooks/useInfiniteScroll'
import { ui } from '../../../utils/uiTokens'



const NewStudentListPage = () => {
    const [searchParams, setSearchParams] = useSearchParams('')
    const { formatMessage } = useIntl()
    const { data: courses = [], isFetching: isFetchingCourses } =
        courseApiSlice.useFetchAllCoursesQuery()
    const {
        data: subjects = [],
    } = subjectApiSlice.useFetchAllSubjectsQuery()
    const [filter, setFilter] = useState('')
    const [search, setSearch] = useState('')
    const [selectedCourse, setSelectedCourse] = useState<string | null>(null)
    const [selectedSubject, setSelectedSubject] = useState<string | null>(null)
    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null)
    const [type, setType] = useState(searchParams.get('type') || "PAID_ONLY")
    const [rows, setRows] = useState<NewStudentState[]>([])
    const [totalCount, setTotalCount] = useState(0)
    const [loadedPages, setLoadedPages] = useState(0)
    // Ответы приходят не по порядку: применяем только последний запрос
    const requestIdRef = useRef(0)
    const isLoadingRef = useRef(false)
    // Наблюдатель может сработать до того, как новая порция легла в state,
    // и снова попросить ту же страницу: она бы добавилась в список второй раз
    const loadedPagesRef = useRef(0)

    const [fetchStudents, { isFetching: isFetchingStudents }] =
        studentApiSlice.useLazyFetchFilteredStudentsQuery()

    const [deleteUser, { isLoading: isDeleting }] =
        userApiSlice.useDeleteUserMutation()
    const [resetTemporaryPassword, { isLoading: isReseting }] =
        userApiSlice.useResetTemporaryPasswordMutation()
    const [sendWelcomeMessage, { isLoading: isSendingWelcomeMessage }] =
        userApiSlice.useSendWelcomeMessageMutation()

    const hasMore = rows.length < totalCount

    const loadPage = async (nextPage: number) => {
        if (nextPage > 1 && (isLoadingRef.current || nextPage <= loadedPagesRef.current)) return

        const requestId = ++requestIdRef.current
        isLoadingRef.current = true

        try {
            const response = await fetchStudents({
                page: nextPage - 1,
                size: DEFAULT_PAGE_SIZE,
                type,
                search,
                filter: {
                    courseId: selectedCourse ? Number(selectedCourse) : null,
                    subjectId: selectedSubject ? Number(selectedSubject) : null,
                    language: selectedLanguage ?? null,
                },
            }).unwrap()

            if (requestId !== requestIdRef.current) return

            setRows((prev) => (nextPage === 1
                ? response.data as NewStudentState[]
                : [...prev, ...(response.data as NewStudentState[])]))
            // Короткая страница означает конец списка: расхождение с totalCount
            // иначе гоняло бы наблюдатель по пустым страницам без остановки
            setTotalCount(response.data.length < DEFAULT_PAGE_SIZE
                ? (nextPage - 1) * DEFAULT_PAGE_SIZE + response.data.length
                : response.totalCount)
            setLoadedPages(nextPage)
            loadedPagesRef.current = nextPage
        } catch (error) {
            // Без остановки наблюдатель повторял бы упавший запрос бесконечно
            if (requestId === requestIdRef.current) setTotalCount((nextPage - 1) * DEFAULT_PAGE_SIZE)
        } finally {
            if (requestId === requestIdRef.current) isLoadingRef.current = false
        }
    }

    const sentinelRef = useInfiniteScroll(
        () => loadPage(loadedPages + 1),
        hasMore && !isFetchingStudents && loadedPages > 0,
    )

    const handleDelete = async (id: number) => {
        try {
            await deleteUser(id).unwrap()
            toast.success(formatMessage({ id: 'deletedSuccess' }))
            loadPage(1)
        } catch (error) { }
    }

    const handleResetPassword = async (id: number) => {
        try {
            await resetTemporaryPassword(id).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            loadPage(1)
        } catch (error) { }
    }

    const handleSendWelcomeMessage = async (id: number) => {
        try {
            await sendWelcomeMessage(id).unwrap()
            toast.success(formatMessage({ id: 'sentSuccess' }))
            loadPage(1)
        } catch (error) { }
    }

    useEffect(() => {
        setSearchParams({ filter: search, type })

        setRows([])
        setTotalCount(0)
        setLoadedPages(0)
        loadedPagesRef.current = 0
        loadPage(1)
    }, [type, search, selectedCourse, selectedSubject, selectedLanguage])

    const handleChangeType = (e, newType) => {
        setType(newType)
    }

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'students' })}</H3>
                    </Title>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <SingleSelect
                            value={selectedCourse ? Number(selectedCourse) : ''}
                            placeholder={formatMessage({ id: 'course' })}
                            handleChange={(e) => setSelectedCourse(e.target.value ? String(e.target.value) : null)}
                            menuItems={[{ id: 0, title: formatMessage({ id: 'all' }) }, ...courses]}
                            valueField={'id'}
                            loading={isFetchingCourses}
                            small
                        />
                        <SingleSelect
                            value={selectedSubject ? Number(selectedSubject) : ''}
                            placeholder={formatMessage({ id: 'subject' })}
                            handleChange={(e) => setSelectedSubject(e.target.value ? String(e.target.value) : null)}
                            menuItems={[{ id: 0, title: formatMessage({ id: 'all' }), }, ...subjects]}
                            valueField={'id'}
                            small
                        />
                        <SingleSelect
                            value={selectedLanguage || ''}
                            placeholder={formatMessage({ id: 'language' })}
                            handleChange={(e) => setSelectedLanguage(e.target.value ? String(e.target.value) : null)}
                            menuItems={[{ id: 0, title: formatMessage({ id: 'all' }) }, { id: 'KZ', title: 'KZ' }, { id: 'RU', title: 'RU' }]}
                            valueField={'id'}
                            small
                        />
                    </Box>



                    <Box sx={styles.filterBox}>
                        <Tabs value={type} onChange={handleChangeType} aria-label="lab API tabs example">
                            <Tab label={formatMessage({ id: 'paid_only' })} value="PAID_ONLY" />
                            <Tab label={formatMessage({ id: 'studied' })} value="STUDIED" />
                            <Tab label={formatMessage({ id: 'all' })} value="ALL" />
                        </Tabs>
                    </Box>

                    <AddStudentDialog refetchStudents={() => loadPage(1)} />
                </Header>

                <SearchBox>
                    <CustomInput
                        sx={{ width: "400px" }}
                        margin="none"
                        type="text"
                        placeholder={formatMessage({ id: 'search' })}
                        size="small"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                setSearch(filter)
                            }
                        }}
                    />

                    <Button
                        variant="contained"
                        onClick={() => setSearch(filter)}
                    >
                        {formatMessage({ id: 'search' })}
                    </Button>
                </SearchBox>

                {(isFetchingStudents && loadedPages === 0) || isReseting || isDeleting ? (
                    <LinearProgress />
                ) : (
                    <>
                        <StudentsTable
                            studentsData={rows}
                            isFetching={isFetchingStudents}
                            isLoadingUsersInfo={false}
                            onDelete={handleDelete}
                            onResetPassword={handleResetPassword}
                            onSendWelcomeMessage={handleSendWelcomeMessage}
                        />

                        <Sentinel ref={sentinelRef}>
                            {isFetchingStudents && <CircularProgress size={22} />}
                        </Sentinel>
                    </>
                )}
            </Content>
        </Wrapper>
    )
}

export default NewStudentListPage


const styles = {
    filterBox: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 2,
    },
    totalSum: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
    },
    totalInfoBox: {
        fontSize: '18px',
        color: ui.ink,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
}



const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Sentinel = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 48px;
`

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: ${ui.card};
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`

const SearchBox = styled(Box)`
    display: flex; 
    align-items: center;
    gap: 8px;
    margin: 8px 0;
`
